import { Panel, DividedBox, ContentLoader, formatNumber, Typography } from '@dev/ee-components';
import { isEmpty, map } from 'lodash-es';
import React from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import {
    AbSplitHeaderControlsInterface,
    PerformanceStatsType,
    PickedCampaignInterface,
    SendCampaignInfoType,
} from '@/store/campaigns/types';
import useMobile from '@/services/hooks/useMobile';
import { ApplicationState } from '@/store';
import { StyledProps } from '@/components/Campaigns/CampaignsInterface';
import { getCampaignSimpleStatValue } from '../../helperFunctions';
import { ContentLoaderWrapper } from '../..';
import { DisabledTrackingAlert } from '../../../components/DisabledTrackingAlert';

const PanelsWrapper = styled.div<StyledProps>`
    width: 100%;
    display: flex;
    flex-direction: ${({ isMobile }): string => (isMobile ? 'column' : 'row')};
    margin: ${({ isMobile }): string => (isMobile ? '16px 0 0 0' : '32px 0')};
    justify-content: space-between;

    ${({ isMobile }): string =>
        isMobile
            ? `
     > div {
         margin: 16px 0px;
     }
    `
            : `
    .GridCart {
        flex: 0.23;
    }
    `};
`;

const PerformanceDividedWrapper = styled.div<StyledProps>`
    padding: ${({ isMobile }): string => isMobile && '16px 0'};
    .row {
        margin: 0;
    }
    .dividedstats {
        .MuiTypography-root {
            div {
                display: inline;
            }
        }
    }
`;
const PanelSkeletonWrapper = styled.div`
    min-width: 150px;
    flex: 1;
`;
const PanelInlineValue = styled.div`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    .MuiTypography-h1 {
        margin-right: 12px;
    }
`;
const PerformanceStatsPanelDescription = (props: {
    pickedCampaignStateNumber: number;
    pickedCampaignStatePercentage: number;
    labelName: string;
    isTracked: boolean;
}): JSX.Element => {
    const { t } = useTranslation();
    const { pickedCampaign, loading } = useSelector((state: ApplicationState) => state.campaigns);
    const { pickedCampaignStateNumber, pickedCampaignStatePercentage, labelName, isTracked } = props;

    return !loading && !isEmpty(pickedCampaign.campaign) ? (
        <>
            <PanelInlineValue>
                <Typography variant={'h1'}>
                    {isTracked ? formatNumber(pickedCampaignStateNumber || 0) : t('na')}
                </Typography>
                <Typography variant={'h3'}>{t(labelName)}</Typography>
            </PanelInlineValue>
            <Typography variant="body1" bodyVariant={'help'}>
                {isTracked ? `${pickedCampaignStatePercentage}%` || '0%' : t('trackingdisabled')}
            </Typography>
        </>
    ) : (
        <PanelSkeletonWrapper>
            <ContentLoader mode={'row'} height={18} width="50%" />
            <ContentLoader mode={'row'} height={14} width="100%" />
        </PanelSkeletonWrapper>
    );
};
const SendCampaignInfo = (
    pickedCampaign: PickedCampaignInterface,
    t: TFunction,
    loading: boolean,
    abSplitHeaderControls: AbSplitHeaderControlsInterface,
    isOpensTracked: boolean,
    isClicksTracked: boolean,
    isMobile: boolean
): SendCampaignInfoType[][] => {
    const deliveredPercentage = getCampaignSimpleStatValue(
        pickedCampaign?.campaign,
        'DeliveredPercentage',
        pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
        abSplitHeaderControls.currentTemplate,
        abSplitHeaderControls.displayMode
    );
    const deliveredCount = getCampaignSimpleStatValue(
        pickedCampaign?.campaign,
        'Delivered',
        pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
        abSplitHeaderControls.currentTemplate,
        abSplitHeaderControls.displayMode
    );
    const openedPercentage = getCampaignSimpleStatValue(
        pickedCampaign?.campaign,
        'OpenedPercentage',
        pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
        abSplitHeaderControls.currentTemplate,
        abSplitHeaderControls.displayMode
    );

    const complaintsPercentage = getCampaignSimpleStatValue(
        pickedCampaign?.campaign,
        'ComplaintsPercentage',
        pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
        abSplitHeaderControls.currentTemplate,
        abSplitHeaderControls.displayMode
    );

    const complaintsCount = getCampaignSimpleStatValue(
        pickedCampaign?.campaign,
        'Complaints',
        pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
        abSplitHeaderControls.currentTemplate,
        abSplitHeaderControls.displayMode
    );

    const clickedPercentage = getCampaignSimpleStatValue(
        pickedCampaign?.campaign,
        'ClickedPercentage',
        pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
        abSplitHeaderControls.currentTemplate,
        abSplitHeaderControls.displayMode
    );

    const deliveredInfo = {
        label: <Typography variant="body2">{t('delivered')}</Typography>,
        value:
            !loading && !isEmpty(pickedCampaign.campaign) ? (
                <div className="dividedstats">
                    <Typography variant="body1" bodyVariant={'help'} display="inline">
                        {deliveredPercentage}%{' '}
                    </Typography>
                    <Typography display="inline" variant="h4">
                        {formatNumber(deliveredCount)}
                    </Typography>
                </div>
            ) : (
                <ContentLoaderWrapper>
                    <ContentLoader mode={'row'} height={19} />
                </ContentLoaderWrapper>
            ),
    };
    const openRateInfo = {
        label: <Typography variant="body2">{t('openrate')}</Typography>,
        value:
            !loading && !isEmpty(pickedCampaign.campaign) ? (
                <Typography variant="h4">{isOpensTracked ? `${openedPercentage}%` : t('trackingdisabled')}</Typography>
            ) : (
                <ContentLoaderWrapper>
                    <ContentLoader mode={'row'} height={19} />
                </ContentLoaderWrapper>
            ),
    };
    const complaintInfo = {
        label: <Typography variant="body2">{t('complained')}</Typography>,
        value:
            !loading && !isEmpty(pickedCampaign.campaign) ? (
                <div className="dividedstats">
                    <Typography variant="body1" bodyVariant={'help'} display="inline">
                        {complaintsPercentage}%{' '}
                    </Typography>
                    <Typography display="inline" variant="h4">
                        {formatNumber(complaintsCount)}
                    </Typography>
                </div>
            ) : (
                <ContentLoaderWrapper>
                    <ContentLoader mode={'row'} height={19} />
                </ContentLoaderWrapper>
            ),
    };
    const clicksTrackedInfo = {
        label: <Typography variant="body2">{t('clickrate')}</Typography>,
        value:
            !loading && !isEmpty(pickedCampaign.campaign) ? (
                <Typography variant="h4">
                    {isClicksTracked ? `${clickedPercentage}%` : t('trackingdisabled')}
                </Typography>
            ) : (
                <ContentLoaderWrapper>
                    <ContentLoader mode={'row'} height={19} />
                </ContentLoaderWrapper>
            ),
    };

    if (isMobile) {
        return [
            [deliveredInfo, complaintInfo],
            [clicksTrackedInfo, openRateInfo],
        ];
    }

    return [
        [deliveredInfo, openRateInfo],
        [complaintInfo, clicksTrackedInfo],
    ];
};

export const PerformanceStats = (props: PerformanceStatsType): JSX.Element => {
    const { campaigns, isClicksTracked, isOpensTracked } = props;
    const { pickedCampaign, loading, abSplitHeaderControls } = campaigns;
    const { t } = useTranslation();
    const isMobile = useMobile();
    const panelProps = isMobile && { ...{ headerPadding: '32px' } };
    const performanceRows = SendCampaignInfo(
        pickedCampaign,
        t,
        loading,
        abSplitHeaderControls,
        isOpensTracked,
        isClicksTracked,
        isMobile
    );

    return (
        <div style={{ margin: '32px 0' }}>
            {!loading && !isEmpty(pickedCampaign.campaign) && <DisabledTrackingAlert />}
            <PanelsWrapper isMobile={isMobile}>
                <Panel
                    description={
                        <PerformanceStatsPanelDescription
                            pickedCampaignStateNumber={getCampaignSimpleStatValue(
                                pickedCampaign?.campaign,
                                'Opened',
                                pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
                                abSplitHeaderControls.currentTemplate,
                                abSplitHeaderControls.displayMode
                            )}
                            pickedCampaignStatePercentage={getCampaignSimpleStatValue(
                                pickedCampaign?.campaign,
                                'OpenedPercentage',
                                pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
                                abSplitHeaderControls.currentTemplate,
                                abSplitHeaderControls.displayMode
                            )}
                            labelName={'opened'}
                            isTracked={isOpensTracked}
                        />
                    }
                    {...panelProps}
                />
                <Panel
                    description={
                        <PerformanceStatsPanelDescription
                            pickedCampaignStateNumber={getCampaignSimpleStatValue(
                                pickedCampaign?.campaign,
                                'Clicked',
                                pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
                                abSplitHeaderControls.currentTemplate,
                                abSplitHeaderControls.displayMode
                            )}
                            pickedCampaignStatePercentage={getCampaignSimpleStatValue(
                                pickedCampaign?.campaign,
                                'ClickedPercentage',
                                pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
                                abSplitHeaderControls.currentTemplate,
                                abSplitHeaderControls.displayMode
                            )}
                            labelName={'clicked'}
                            isTracked={isClicksTracked}
                        />
                    }
                    {...panelProps}
                />
                <Panel
                    description={
                        <PerformanceStatsPanelDescription
                            pickedCampaignStateNumber={getCampaignSimpleStatValue(
                                pickedCampaign?.campaign,
                                'Bounced',
                                pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
                                abSplitHeaderControls.currentTemplate,
                                abSplitHeaderControls.displayMode
                            )}
                            pickedCampaignStatePercentage={getCampaignSimpleStatValue(
                                pickedCampaign?.campaign,
                                'BouncedPercentage',
                                pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
                                abSplitHeaderControls.currentTemplate,
                                abSplitHeaderControls.displayMode
                            )}
                            labelName={'bounced'}
                            isTracked={true}
                        />
                    }
                    {...panelProps}
                />
                <Panel
                    description={
                        <PerformanceStatsPanelDescription
                            pickedCampaignStateNumber={getCampaignSimpleStatValue(
                                pickedCampaign?.campaign,
                                'Unsubscribed',
                                pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
                                abSplitHeaderControls.currentTemplate,
                                abSplitHeaderControls.displayMode
                            )}
                            pickedCampaignStatePercentage={getCampaignSimpleStatValue(
                                pickedCampaign?.campaign,
                                'UnsubscribedPercentage',
                                pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
                                abSplitHeaderControls.currentTemplate,
                                abSplitHeaderControls.displayMode
                            )}
                            labelName={'unsubscribed'}
                            isTracked={true}
                        />
                    }
                    {...panelProps}
                />
            </PanelsWrapper>
            {isMobile ? (
                map(performanceRows, (row) => (
                    <PerformanceDividedWrapper isMobile={true}>
                        <DividedBox rows={[row]} />
                    </PerformanceDividedWrapper>
                ))
            ) : (
                <PerformanceDividedWrapper>
                    <DividedBox rows={performanceRows} />
                </PerformanceDividedWrapper>
            )}
        </div>
    );
};
